import { Inject, Injectable, Optional } from '@angular/core';
import posthog from 'posthog-js';
import { Store } from '@ngrx/store';
import {
  AnalyticsConfig,
  AnalyticsEventType,
  ANALYTICS_CONFIG
} from '../models/event-types';
import { UserSelector } from '@tix/auth/state';

@Injectable({
  providedIn: 'root'
})
export class TixAnalyticsService {
  private initialized = false;
  private currentUserId: string | null = null;

  constructor(
    @Optional() @Inject(ANALYTICS_CONFIG) private config: AnalyticsConfig,
    private store: Store
  ) {
    if (!this.config) {
      console.error(
        '[Analytics] No configuration provided. Analytics will be disabled.'
      );
      return;
    }

    if (!this.config.postHogApiKey) {
      console.error(
        '[Analytics] No PostHog API key provided. Analytics will be disabled.'
      );
      return;
    }

    // Initialize PostHog
    this.init();

    // Subscribe to auth user
    this.store.select(UserSelector.getAuthenticatedUser).subscribe(user => {
      this.currentUserId = user?.uid ?? null;
      if (this.currentUserId && this.initialized) {
        this.identifyUser(this.currentUserId);
      }
    });
  }

  init(): void {
    if (!this.config?.enabled) return;

    try {
      posthog.init(this.config.postHogApiKey, {
        api_host: this.config.postHogHost || 'https://app.posthog.com',
        loaded: posthog => {
          if (this.currentUserId) {
            this.identifyUser(this.currentUserId);
          }
        }
      });
      this.initialized = true;

      if (this.config.debug) {
        console.log('[Analytics] Initialized PostHog');
      }
    } catch (error) {
      console.error('[Analytics] Failed to initialize PostHog:', error);
    }
  }

  captureEvent(event: AnalyticsEventType, data?: Record<string, any>): void {
    if (!this.initialized || !this.config?.enabled) return;

    try {
      posthog.capture(event, {
        ...data,
        userId: this.currentUserId || 'anonymous'
      });

      if (this.config.debug) {
        console.log('[Analytics] Event captured:', event, data);
      }
    } catch (error) {
      console.error('[Analytics] Failed to capture event:', error);
    }
  }

  identifyUser(userId: string, traits?: Record<string, any>): void {
    if (!this.initialized || !this.config?.enabled) return;

    try {
      posthog.identify(userId, traits);

      if (this.config.debug) {
        console.log('[Analytics] User identified:', userId, traits);
      }
    } catch (error) {
      console.error('[Analytics] Failed to identify user:', error);
    }
  }

  reset(): void {
    if (!this.initialized || !this.config?.enabled) return;

    try {
      posthog.reset();
      if (this.config.debug) {
        console.log('[Analytics] Analytics reset');
      }
    } catch (error) {
      console.error('[Analytics] Failed to reset:', error);
    }
  }
}
